// Pure notification logic shared by the send-notification edge function (Deno)
// and the React UI (preview / live recipient count). NO imports, NO platform APIs.

export interface NotificationFilters {
  payment_status?: string[] | null
  needs_transport?: boolean | null
  room_ids?: string[] | null
  sex?: string | null
  min_age?: number | null
  max_age?: number | null
}

export interface RegistrationLike {
  id: string
  payment_status: string
  needs_transport: boolean | null
  sex: string | null
  date_of_birth: string | null
}

export function computeAge(
  dateOfBirth: string | null | undefined,
  at: Date = new Date(),
): number | null {
  if (!dateOfBirth) return null
  const dob = new Date(dateOfBirth + 'T00:00:00Z')
  if (isNaN(dob.getTime())) return null
  let age = at.getUTCFullYear() - dob.getUTCFullYear()
  const monthDiff = at.getUTCMonth() - dob.getUTCMonth()
  if (monthDiff < 0 || (monthDiff === 0 && at.getUTCDate() < dob.getUTCDate())) age--
  return age
}

export function filterRegistrations<T extends RegistrationLike>(
  registrations: T[],
  filters: NotificationFilters,
  roomIdByRegistration: Record<string, string | undefined>,
  now: Date = new Date(),
): T[] {
  return registrations.filter(r => {
    if (r.payment_status === 'Cancelled') return false
    if (filters.payment_status && filters.payment_status.length > 0
        && !filters.payment_status.includes(r.payment_status)) return false
    if (filters.needs_transport !== null && filters.needs_transport !== undefined
        && Boolean(r.needs_transport) !== filters.needs_transport) return false
    if (filters.room_ids && filters.room_ids.length > 0) {
      const roomId = roomIdByRegistration[r.id]
      if (!roomId || !filters.room_ids.includes(roomId)) return false
    }
    if (filters.sex && r.sex !== filters.sex) return false
    if (filters.min_age != null || filters.max_age != null) {
      const age = computeAge(r.date_of_birth, now)
      if (age === null) return false
      if (filters.min_age != null && age < filters.min_age) return false
      if (filters.max_age != null && age > filters.max_age) return false
    }
    return true
  })
}

export interface VariableContext {
  registration: {
    full_name: string
    needs_transport: boolean | null
    amount_owed: number | null
    amount_paid: number | null
  }
  camp: {
    name: string
    start_date: string | null
    end_date: string | null
    location: string | null
    map_link: string | null
    arrival_time: string | null
    transport_meeting_point: string | null
    transport_meeting_time: string | null
    packing_list: string | null
  }
  room: { name: string; photo_url: string | null } | null
}

export const KNOWN_VARIABLES = [
  'name', 'camp_name', 'start_date', 'end_date', 'location', 'map_link',
  'arrival_time', 'room_name', 'room_photo', 'packing_list', 'transport_info',
  'amount_owed', 'amount_paid', 'balance',
]

function esc(s: string): string {
  return s.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;')
}

function formatMoney(n: number | null | undefined): string {
  const value = typeof n === 'number' && isFinite(n) ? n : 0
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

function formatSpanishDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00Z')
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
}

function renderPackingList(list: string | null, mode: 'html' | 'text'): string {
  if (!list) return ''
  const items = list.split('\n').map(s => s.trim()).filter(Boolean)
  if (items.length === 0) return ''
  if (mode === 'html') return `<ul>${items.map(i => `<li>${esc(i)}</li>`).join('')}</ul>`
  return items.map(i => `- ${i}`).join('\n')
}

function renderTransportInfo(ctx: VariableContext, mode: 'html' | 'text'): string {
  const { camp } = ctx
  if (mode === 'html') {
    const lines = ctx.registration.needs_transport
      ? [
          `Punto de encuentro: ${esc(camp.transport_meeting_point ?? '')}`,
          `Hora de salida: ${esc(camp.transport_meeting_time ?? '')}`,
        ]
      : [
          `Lugar: ${esc(camp.location ?? '')}`,
          camp.map_link ? `Mapa: <a href="${esc(camp.map_link)}">${esc(camp.map_link)}</a>` : '',
          `Hora de llegada: ${esc(camp.arrival_time ?? '')}`,
        ]
    return lines.filter(Boolean).join('<br>')
  }
  const lines = ctx.registration.needs_transport
    ? [
        `Punto de encuentro: ${camp.transport_meeting_point ?? ''}`,
        `Hora de salida: ${camp.transport_meeting_time ?? ''}`,
      ]
    : [
        `Lugar: ${camp.location ?? ''}`,
        camp.map_link ? `Mapa: ${camp.map_link}` : '',
        `Hora de llegada: ${camp.arrival_time ?? ''}`,
      ]
  return lines.filter(Boolean).join('\n')
}

function resolveOne(variable: string, ctx: VariableContext, mode: 'html' | 'text'): string | null {
  const { registration: reg, camp, room } = ctx
  const e = mode === 'html' ? esc : (s: string) => s
  switch (variable) {
    case 'name':           return e(reg.full_name ?? '')
    case 'camp_name':      return e(camp.name ?? '')
    case 'start_date':     return e(formatSpanishDate(camp.start_date))
    case 'end_date':       return e(formatSpanishDate(camp.end_date))
    case 'location':       return e(camp.location ?? '')
    case 'map_link':       return e(camp.map_link ?? '')
    case 'arrival_time':   return e(camp.arrival_time ?? '')
    case 'room_name':      return e(room?.name ?? '')
    case 'room_photo':
      if (!room?.photo_url) return ''
      return mode === 'html'
        ? `<img src="${esc(room.photo_url)}" alt="${esc(room.name)}" style="max-width:100%;border-radius:12px;margin:8px 0;" />`
        : room.photo_url
    case 'packing_list':   return renderPackingList(camp.packing_list, mode)
    case 'transport_info': return renderTransportInfo(ctx, mode)
    case 'amount_owed':    return formatMoney(reg.amount_owed)
    case 'amount_paid':    return formatMoney(reg.amount_paid)
    case 'balance':        return formatMoney((reg.amount_owed ?? 0) - (reg.amount_paid ?? 0))
    default:               return null // unknown variable — leave as-is
  }
}

export function resolveVariables(text: string, ctx: VariableContext, mode: 'html' | 'text'): string {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) => {
    const resolved = resolveOne(name, ctx, mode)
    return resolved === null ? match : resolved
  })
}

export function extractVariables(text: string): string[] {
  const seen: string[] = []
  for (const m of text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
    if (!seen.includes(m[1])) seen.push(m[1])
  }
  return seen
}

export function getVariableWarnings(
  text: string,
  ctxs: VariableContext[],
): { variable: string; missingCount: number }[] {
  const used = extractVariables(text).filter(v => KNOWN_VARIABLES.includes(v))
  const warnings: { variable: string; missingCount: number }[] = []
  for (const variable of used) {
    const missingCount = ctxs.filter(c => (resolveOne(variable, c, 'text') ?? '') === '').length
    if (missingCount > 0) warnings.push({ variable, missingCount })
  }
  return warnings
}
